import type {
  RecordJobData,
  ActionOutputData,
} from "@smartsuite/automation-engine-api";
import { observer } from "mobx-react-lite";
import { useEffect, useMemo, useState } from "react";
import { translateAction } from "../../../../../locales/i18n";
import { useRootStore } from "../../../../../stores/hooks";
import { ActionLabel } from "../../../../../utils/constants";
import { getErrorMessage } from "../../../ActivityGrid/ActivityGrid.helpers";
import { ActionError } from "../ActionError/ActionError";
import { ActivityDetailsGroup } from "../ActivityDetailsGroup/ActivityDetailsGroup";
import type { ActionCandidate } from "../../../../../types/candidate";
import { isOldTypejob } from "../../utils";
import { getActionRecordCount } from "../../../shared/status-walk";
import { useRecordJobData } from "../../hooks/useRecordJobData";
import { GeneralWebhookGroupWrapper } from "../GenerateWebhookGroupWrapper/GenerateWebhookGroupWrapper";

interface GenerateWebhookProps {
  action: ActionOutputData;
  /** Top-level group the action came from; record-job paths are keyed by it. */
  groupInstanceId?: number;
  onDetailsClick?: () => void;
}

export const GenerateWebhookGroup = observer<GenerateWebhookProps>(
  ({ action, groupInstanceId = 0, onDetailsClick }) => {
    const { integrations, automations, jobDetails } = useRootStore();
    const selectedIntegration = integrations.list.find(
      (i) => i.integrationId === action.actionReference?.integrationId,
    );
    const selectedAction = selectedIntegration?.actions.find(
      (a) => a.actionId === action.actionReference?.actionId,
    );
    const [selectedActionFallback, setSelectedActionFallback] = useState<
      ActionCandidate | undefined
    >();
    const [isUpdateAfterFindAction, setIsUpdateAfterFindAction] =
      useState<boolean>(false);
    const [recordJobsFiltered, setRecordJobsFiltered] = useState<
      RecordJobData[]
    >([]);

    const [recordJobData, setRecordJobData] = useState<
      Record<string, RecordJobData>
    >({});

    useEffect(() => {
      setSelectedActionFallback(
        automations.candidate.allActions.find(
          (actionInput: ActionCandidate) =>
            actionInput.actionReference?.instanceId ===
            action.actionReference?.instanceId,
        ),
      );
    }, [automations.candidate]);

    useEffect(() => {
      if (
        action.actionReference?.actionId === ActionLabel.updateRecord &&
        action.recordListState !== undefined
      ) {
        setIsUpdateAfterFindAction(true);
      }
    }, [action]);

    useEffect(() => {
      const recordJobDatas = jobDetails.recordJobData;
      setRecordJobsFiltered(
        recordJobDatas.filter(
          (recordJobData) =>
            recordJobData.actionReference?.instanceId ===
            action.actionReference?.instanceId,
        ),
      );
    }, [isUpdateAfterFindAction, JSON.stringify(jobDetails.recordJobData)]);

    const { fetchRecordJobData } = useRecordJobData({
      recordJobData,
      setRecordJobData,
    });

    const status = action.status.oneofKind === "ok" ? "is_success" : "is_error";

    const errorMessage = getErrorMessage(action, jobDetails.job);
    const oldTypeJob = useMemo(
      () => isOldTypejob(jobDetails),
      [jobDetails.job],
    );
    const onDetailsClickWithFetch = async (
      recordJodId: string,
    ): Promise<void> => {
      await fetchRecordJobData(recordJodId);
    };

    const recordCount = getActionRecordCount(action, jobDetails.job);

    return (
      <ActivityDetailsGroup
        label={
          selectedAction !== undefined ? translateAction(selectedAction) : ""
        }
        status={status}
      >
        {status === "is_error" && <ActionError errorMessage={errorMessage} />}

        {!oldTypeJob &&
          recordCount > 0 &&
          Array.from({ length: recordCount }).map((_, idx) => {
            const recordJodId = `jobs/${jobDetails.job.jobId}/group-${groupInstanceId}/action-${
              action.actionReference?.instanceId ?? ""
            }/record-${idx}`;
            return (
              <GeneralWebhookGroupWrapper
                key={idx}
                index={idx}
                jobDetails={jobDetails}
                recordJob={recordJobData[recordJodId]}
                onDetailsClickWithFetch={onDetailsClickWithFetch}
                recordJodId={recordJodId}
                action={action}
                automations={automations}
                selectedActionFallback={selectedActionFallback}
              />
            );
          })}

        {oldTypeJob &&
          recordJobsFiltered.map((recordJob, index) => {
            const recordJodId = `jobs/${jobDetails.job.jobId}/group-0/action-${
              action.actionReference?.instanceId ?? ""
            }/record-${index}`;
            return (
              <GeneralWebhookGroupWrapper
                key={recordJodId}
                index={index}
                jobDetails={jobDetails}
                recordJob={recordJob}
                onDetailsClick={onDetailsClick}
                action={action}
                automations={automations}
                selectedActionFallback={selectedActionFallback}
              />
            );
          })}
      </ActivityDetailsGroup>
    );
  },
);
